/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {FilterDrawerCategory} from '@ion/dls/src/components/drawer/FilterDrawer';
import {orderFilterDrawerCategories} from '@ion/dls/src/components/drawer/utils';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {TimeModes} from '@ion/dls/src/types/types';
import {getRunningTimeModeReport} from '../../../routes/tracking2/helpers';
import {
  historyActiveSelector,
  liveHistoryActiveSelector,
  timeModeSelector,
  trackingModeSelector
} from '../../../store/selectors/tracking-selectors';
import {setTrackingFilterCategoryOrder, setTrackingFilterExpandedIds} from './store';
import {getVisibleTrackingFilterCategories, mergeVisibleTrackingFilterCategoryOrder} from './utils';

interface TrackingFilterHistoryCategoriesArgs {
  categories: FilterDrawerCategory[];
  categoryOrder: string[];
  expandedIds: string[];
}

interface TrackingFilterHistoryCategories {
  // the categories the drawer shows ...
  categories: FilterDrawerCategory[];
  // the shown categories that are expanded ...
  expandedIds: string[];
  // the hidden categories' filters, kept mounted out of sight so their
  // selections still take part in Apply, Clear All and favorites ...
  hiddenContent: ReactNode[];
  onCategoryOrderChange: (categoryOrder: string[]) => void;
  onExpandedIdsChange: (expandedIds: string[]) => void;
}

/**
 * The useIsTrackingHistoryRunning hook tells whether the Time Mode control
 * is running a History report - in the current theme only. It changes as
 * the tracking mode, the time mode or either report's active flag does.
 *
 * ```typescript
 *
 *   import {useIsTrackingHistoryRunning} from './useTrackingFilterHistoryCategories';
 *   ...
 *
 *   const isHistoryRunning = useIsTrackingHistoryRunning();
 *
 * ```
 *
 * @returns `true` while a History report runs in the current theme,
 *          otherwise `false`.
 */
export const useIsTrackingHistoryRunning = () => {
  const timeMode            = useSelector(timeModeSelector);
  const trackingMode        = useSelector(trackingModeSelector);
  const isHistoryActive     = useSelector(historyActiveSelector);
  const isLiveHistoryActive = useSelector(liveHistoryActiveSelector);
  return isCurrentTheme() && getRunningTimeModeReport({
    timeMode,
    trackingMode,
    isHistoryActive,
    isLiveHistoryActive
  }) === TimeModes.history;
};

/**
 * The useTrackingFilterHistoryCategories hook hides the Tracking Filter
 * categories a running History report leaves out - Geofence, Routes and
 * Asset Status - in the current theme only. Their filters stay mounted out
 * of sight, so the selections they hold are neither lost nor dropped from
 * Apply, Clear All or a saved favorite; and a reorder or expansion of the
 * categories still shown is folded back into the full order and expanded
 * set, so the hidden ones return where they were once History ends. While
 * no History report runs - or in the legacy theme - everything is passed
 * through as it is.
 *
 * ```typescript
 *
 *   import useTrackingFilterHistoryCategories from './useTrackingFilterHistoryCategories';
 *   ...
 *
 *   const {
 *     categories,
 *     expandedIds,
 *     hiddenContent,
 *     onCategoryOrderChange,
 *     onExpandedIdsChange
 *   } = useTrackingFilterHistoryCategories({
 *     categories    : allCategories,
 *     categoryOrder,
 *     expandedIds   : allExpandedIds
 *   });
 *
 * ```
 *
 * @param   args The drawer's categories, their saved order, and the ids
 *               expanded.
 * @returns      The categories and expanded ids to show, the hidden
 *               filters' content, and the order and expansion handlers.
 */
export const useTrackingFilterHistoryCategories = ({
  categories,
  categoryOrder,
  expandedIds
}: TrackingFilterHistoryCategoriesArgs): TrackingFilterHistoryCategories => {
  const isHistoryRunning = useIsTrackingHistoryRunning();

  const visibleCategories = useMemo(() => (
    getVisibleTrackingFilterCategories(categories, isHistoryRunning)
  ), [categories, isHistoryRunning]);

  const hiddenContent = useMemo(() => (
    categories
      .filter(category => !visibleCategories.includes(category))
      .map(({id, content}) => <React.Fragment key={id}>{content}</React.Fragment>)
  ), [categories, visibleCategories]);

  const isVisible = useCallback((id: string) => (
    visibleCategories.some(category => category.id === id)
  ), [visibleCategories]);

  // only the shown categories are handed on as expanded - the drawer lets go
  // of any id it cannot see, which would otherwise forget the hidden ones ...
  const visibleExpandedIds = useMemo(() => (
    isHistoryRunning ? expandedIds.filter(isVisible) : expandedIds
  ), [expandedIds, isHistoryRunning, isVisible]);

  const onExpandedIdsChange = useCallback((ids: string[]) => {
    setTrackingFilterExpandedIds(isHistoryRunning
      ? [...ids, ...expandedIds.filter(id => !isVisible(id))]
      : ids);
  }, [expandedIds, isHistoryRunning, isVisible]);

  const onCategoryOrderChange = useCallback((ids: string[]) => {
    setTrackingFilterCategoryOrder(isHistoryRunning
      ? mergeVisibleTrackingFilterCategoryOrder(
        orderFilterDrawerCategories(categories, categoryOrder).map(({id}) => id),
        ids
      )
      : ids);
  }, [categories, categoryOrder, isHistoryRunning]);

  return {
    categories  : visibleCategories,
    expandedIds : visibleExpandedIds,
    hiddenContent,
    onCategoryOrderChange,
    onExpandedIdsChange
  };
};

export default useTrackingFilterHistoryCategories;
